import {SendErrorReportRequestDto} from '@/classes/dto/messenger/request/SendErrorReport.request.dto';
import {SendPermissionRequestRequestDto} from '@/classes/dto/messenger/request/SendPermissionRequest.request.dto';
import {LeanDocument, Types} from 'mongoose';
import {API} from './api.class';
import {FindByIdOptionsRequestDto} from '@/classes/dto/_common/request/FindByIdOptions.request.dto';
import {MailboxSyncDocument} from '@/documentTypes/messenger/MailboxSync.document';
import {MessengerChannelAccessDocument} from '@/documentTypes/messenger/MessengerChannelAccess.document';
import {CreateMailboxSyncRequestDto} from '@/classes/dto/messenger/request/mailbox-sync/CreateMailboxSync.request.dto';
import {UpdateMailboxSyncRequestDto} from '@/classes/dto/messenger/request/mailbox-sync/UpdateMailboxSync.request.dto';
import {FindMailboxSyncRequestDto} from '@/classes/dto/messenger/request/mailbox-sync/FindMailboxSync.request.dto';
import {
    FindMessengerChannelAccessRequestDto,
} from '@/classes/dto/messenger/request/channels/FindMessengerChannelAccess.request.dto';
import {
    SetMessengerChannelAccessRequestDto,
} from '@/classes/dto/messenger/request/channels/SetMessengerChannelAccess.request.dto';
import {
    GetMessengerChannelOptionsResponseDto,
} from '@/classes/dto/messenger/response/channels/GetMessengerChannelOptions.response.dto';
import {
    SenderAreaSettingsResponseDto,
    UpdateSenderAreaSettingsRequestDto,
} from '@/classes/dto/messenger/senderAreas/SenderAreaSettings.dto';
import {
    GetMessengerChannelOverviewResponseDto,
} from '@/classes/dto/messenger/response/channels/GetMessengerChannelOverview.response.dto';
import {
    MessengerMailDocument,
    MessengerMessageDocument,
    MessengerWhatsAppMessageDocument,
} from '@/documentTypes/messenger/messages';
import {FindMessengerMailRequestDto} from '@/classes/dto/messenger/request/FindMessengerMail.request.dto';
import {GetConversationsRequestDto} from '@/classes/dto/messenger/request/messages/GetConversations.request.dto';
import {GetConversationsResponseDto} from '@/classes/dto/messenger/response/messages/GetConversations.response.dto';
import {SetConversationSeenRequestDto} from '@/classes/dto/messenger/request/messages/SetConversationSeen.request.dto';
import {RematchMessagesRequestDto} from '@/classes/dto/messenger/request/messages/RematchMessages.request.dto';
import {SendMessengerMailRequestDto} from '@/classes/dto/messenger/request/SendMessengerMail.request.dto';
import {
    FindMessengerWhatsAppMessageRequestDto,
} from '@/classes/dto/messenger/request/FindMessengerWhatsAppMessage.request.dto';
import {FindMessengerMessagesRequestDto} from '@/classes/dto/messenger/request/FindMessengerMessages.request.dto';
import {
    SendMessengerWhatsAppMessageRequestDto,
} from '@/classes/dto/messenger/request/SendMessengerWhatsAppMessage.request.dto';
import {
    SendMessengerMailTemplateRequestDto,
} from '@/classes/dto/messenger/request/SendMessengerMailTemplate.request.dto';

export class MessengerApi extends API {
    static service = 'messenger';

    /** Communication channels: protocol sync of mailboxes and per-user channel grants */
    static channels = {
        /** Mail addresses and WhatsApp numbers the current user may use in the messenger */
        mine: async (): Promise<GetMessengerChannelOptionsResponseDto> => {
            return this.get('channels/mine');
        },
        overview: async (): Promise<GetMessengerChannelOverviewResponseDto> => {
            return this.get('channels/overview');
        },
        /** "Absender & Anlässe": default sender, own sender + channel mode + standard templates per send occasion */
        senderAreas: {
            /** Old messenger without the endpoint / not reachable: handled by the caller (dialogs behave like before) */
            get: async (): Promise<SenderAreaSettingsResponseDto> => {
                return this.get('channels/sender-areas', undefined, undefined, {handledStatuses: [403, 404, 500, 502, 503]});
            },
            update: async (data: UpdateSenderAreaSettingsRequestDto): Promise<SenderAreaSettingsResponseDto> => {
                return this.put('channels/sender-areas', data);
            },
        },
        mailboxSync: {
            create: async (data: CreateMailboxSyncRequestDto): Promise<Types.ObjectId> => {
                return this.post('channels/mailbox-sync', data);
            },
            find: async (data: FindMailboxSyncRequestDto): Promise<{ mailboxSyncs: LeanDocument<MailboxSyncDocument>[]; maxPage: number }> => {
                return this.get('channels/mailbox-sync', data);
            },
            update: async (id: Types.ObjectId, data: UpdateMailboxSyncRequestDto): Promise<Types.ObjectId> => {
                return this.put(`channels/mailbox-sync/${id}`, data);
            },
            delete: async (id: Types.ObjectId): Promise<Types.ObjectId> => {
                return this.delete(`channels/mailbox-sync/${id}`);
            },
        },
        access: {
            find: async (data: FindMessengerChannelAccessRequestDto): Promise<{
                messengerChannelAccesses: LeanDocument<MessengerChannelAccessDocument>[];
                maxPage: number
            }> => {
                return this.get('channels/access', data);
            },
            /** Replaces all grants of one login user */
            set: async (data: SetMessengerChannelAccessRequestDto): Promise<Types.ObjectId> => {
                return this.put('channels/access', data);
            },
        },
    }

    static messages = {
        getConversations: async (data: GetConversationsRequestDto): Promise<GetConversationsResponseDto> => {
            return this.get('messages/conversations', data);
        },
        /** Conversations with messages newer than the user's last read */
        getUnreadCount: async (channel?: 'mail' | 'whatsApp'): Promise<{ unread: number }> => {
            return this.get('messages/conversations/unread-count', channel ? {channel} : undefined);
        },
        /** „Alle als gelesen“: only the own read state, optionally one channel */
        setAllSeen: async (channel?: 'mail' | 'whatsApp'): Promise<{ marked: number }> => {
            return this.put('messages/conversations/seen-all', channel ? {channel} : {});
        },
        setSeen: async (data: SetConversationSeenRequestDto): Promise<string> => {
            return this.put('messages/conversations/seen', data);
        },
        /** After an address or number was added to a profile: match its messages to that profile */
        rematch: async (data: RematchMessagesRequestDto): Promise<{ matched: number }> => {
            return this.post('messages/rematch', data);
        },
        find: async (data: FindMessengerMessagesRequestDto): Promise<{
            messengerMessages: LeanDocument<MessengerMessageDocument>[],
            maxPage: number
        }> => {
            return this.get('messages', data);
        },
        findById: async (id: Types.ObjectId, options?: FindByIdOptionsRequestDto): Promise<LeanDocument<MessengerMessageDocument>> => {
            return this.get(`messages/${id}`, options);
        },
        /** One message for a preview: missing right or message are no error for the page */
        preview: async (id: string): Promise<LeanDocument<MessengerMessageDocument>> => {
            return this.get(`messages/${id}`, undefined, undefined, {handledStatuses: [403, 404]});
        },
        /** Card „Verlauf“: one message of the student or their parents, without channel grant (right „Verlauf je Kind: Nachrichten lesen“, protocolled) */
        forStudent: async (id: string, student: string): Promise<any> => {
            return this.get(`messages/${id}/for-student/${student}`, undefined, undefined, {handledStatuses: [403, 404]});
        },
        mails: {
            findById: async (id: Types.ObjectId, options?: FindByIdOptionsRequestDto): Promise<LeanDocument<MessengerMailDocument>> => {
                return this.get(`mails/${id}`, options);
            },
            find: async (data: FindMessengerMailRequestDto): Promise<{
                messengerMails: LeanDocument<MessengerMailDocument>[],
                maxPage: number
            }> => {
                return this.get('mails', data);
            },
            sendMail: async (data: SendMessengerMailRequestDto): Promise<Types.ObjectId> => {
                return this.post('mails', data);
            },
            /** „Vorschau“ of the composer: subject + text in the brand frame, as the send renders it */
            previewFrame: async (data: { subject: string; message: string }): Promise<{ html: string }> => {
                return this.post('mails/preview-frame', data);
            },
            /** Attachment of a mail (index in its `attachments`) – allowed for whoever may read the mail */
            attachment: async (mailId: Types.ObjectId | string, index: number): Promise<Blob> => {
                return this.get(`mails/${mailId}/attachments/${index}`, {}, 'blob');
            },
            sendMailTemplate: async (data: SendMessengerMailTemplateRequestDto): Promise<Types.ObjectId> => {
                return this.post('mails/template', data);
            },
            /** Error report of the error dialog, sent from the system mailbox to the developers */
            sendErrorReport: async (data: SendErrorReportRequestDto): Promise<void> => {
                return this.post('mails/error-report', data);
            },
            /** Permission request of the "Zugriff verweigert" dialog, same recipients as the error report */
            sendPermissionRequest: async (data: SendPermissionRequestRequestDto): Promise<void> => {
                return this.post('mails/permission-request', data);
            },
        },
        whatsApp: {
            sendMessage: async (data: SendMessengerWhatsAppMessageRequestDto): Promise<Types.ObjectId> => {
                return this.post('whats-app/send-message', data);
            },
            findById: async (id: Types.ObjectId, options?: FindByIdOptionsRequestDto): Promise<LeanDocument<MessengerWhatsAppMessageDocument>> => {
                return this.get(`whats-app/${id}`, options);
            },
            find: async (data: FindMessengerWhatsAppMessageRequestDto): Promise<{
                messengerWhatsAppMessages: LeanDocument<MessengerWhatsAppMessageDocument>[],
                maxPage: number
            }> => {
                return this.get('whats-app', data);
            },
        },
    }

}
